/**
 * 🦴 **WHICH BONE IS THE HEAD?** — reading a quadruped's anatomy off its own
 * skeleton, so that rigging the next animal is one command and no thinking.
 *
 * 🎯 **haugur, 2026-09-23:** *"we have a loooot of animals to add to the game so
 * this process cant be this complicated."* **This file is the answer to that
 * sentence.**
 *
 * ## 🔴 Why this cannot be a hand-written list
 *
 * The auto-rigger names its bones `bone_0`, `bone_1`, … — **a number is not an
 * anatomy.** And the numbering is not stable: **the same sheep body, rigged
 * twice at two mesh densities, came back with two different skeletons.**
 *
 * | | rig ① (15,765 triangles) | rig ② (90,996 triangles) |
 * |---|---|---|
 * | bones | 23 | 23 |
 * | tree depth | 8 | 5 |
 * | `bone_1` | present | **absent entirely** |
 * | tail bones | none | 2 |
 * | ear bones | none | 2 |
 * | legs hang off | the pelvis *and* the chest | **all four off the pelvis** |
 *
 * 👉 ***So a bone map written down by hand is wrong the next time anybody
 * re-rolls a body*** — and it would not fail loudly. It would walk the sheep on
 * its ears. ⚠️ **That is this repo's oldest shape of fault**: a name derived
 * from a proxy, wrong the day a second thing shares it.
 *
 * ## ✅ What it reads instead
 *
 * **The shape of the animal.** A quadruped standing in a rest pose puts its
 * four feet at the BOTTOM in four corners, its head at the FRONT and high, and
 * its tail at the BACK. **All of that is in the numbers**, and none of it
 * depends on what anybody called a bone.
 *
 * 🔴 **It takes plain numbers, not three.js objects**, so it can be tested in
 * Node with no DOM and no renderer — **against the two real skeletons above**,
 * which is the only kind of test worth having here. *A synthetic case has no
 * canyon walls*, and this project has paid for that three times.
 */

/** One bone, as the glTF skin gives it: a name, a parent, a rest position. */
export interface RigBone {
  readonly name: string;
  /** index into the same array, or −1 for a root */
  readonly parent: number;
  /** WORLD position in the rest pose — full TRS, never translations alone */
  readonly world: readonly [number, number, number];
}

/** Which array index means length, up and across. */
export interface RigAxes {
  readonly length: 0 | 1 | 2;
  readonly up: 0 | 1 | 2;
  readonly across: 0 | 1 | 2;
}

export interface QuadAnatomy {
  readonly axes: RigAxes;
  /** pelvis → chest, ordered back to front */
  readonly spine: readonly number[];
  /** base of neck → head, ordered up the chain. Ears are NOT in it */
  readonly neck: readonly number[];
  /** the leaf pairs that hang off the head — an ear wiggle, later */
  readonly ears: readonly number[];
  /** back to front; `null` when the rig has no tail bones at all */
  readonly tail: readonly number[] | null;
  /** each leg root → hoof. `side` is +1 or −1 on the across axis */
  readonly legs: readonly {
    readonly bones: readonly number[];
    readonly fore: boolean;
    readonly side: 1 | -1;
  }[];
  /** bones that are none of the above — left alone rather than guessed at */
  readonly other: readonly number[];
}

/**
 * 📐 **The axes, off the MESH's own bounding box.**
 *
 * A sheep is 1.07 long · 0.85 tall · 0.42 wide, so the **longest** extent is its
 * length and the **larger of the other two** is its height. ⚠️ **That holds for
 * every land animal this game will have** — nothing walks that is wider than it
 * is tall. 🔴 **It would NOT hold for a bird with open wings**, and that is
 * written here rather than discovered later.
 */
export function axesFromMeshSize(size: readonly [number, number, number]): RigAxes {
  // eslint-disable-next-line security/detect-object-injection -- a loop counter or a fixed 0|1|2 axis index into a plain array, as `legsplit.ts` and `burgeom.ts` do.
  const rank = ([0, 1, 2] as const).slice().sort((a, b) => size[b] - size[a]);
  return { length: rank[0], up: rank[1], across: rank[2] } as RigAxes;
}

/**
 * 🦴 Read the anatomy off the skeleton.
 *
 * 🔴 **The whole method in five lines**, and every step is a measurement:
 * ① a *chain* is a run of bones each with exactly one child, ending at a leaf;
 * ② the **four chains whose tips are lowest are the legs** — feet are on the
 *    ground and nothing else is;
 * ③ fore or hind by how far forward the leg's root sits;
 * ④ the **neck** is the walk UP from the forwardmost bone to the first
 *    ancestor with a leg on it, minus any pair of leaf siblings at the end,
 *    which are the **ears**;
 * ⑤ the **tail** is whatever chain is left reaching furthest BACK.
 *
 * ⚠️ **Step ④ said "the chain reaching furthest forward" until a test failed.**
 * In rig ② that chain **is one ear**, so the neck came back empty. The comment
 * is corrected here rather than left describing a method this code no longer
 * uses — *a comment carrying the wrong IDEA is the expensive kind.*
 */
export function deriveQuadAnatomy(bones: readonly RigBone[], axes: RigAxes): QuadAnatomy {
  const n = bones.length;
  /* 🔴 Every index into these is a loop counter or a parent already range
     checked, but `noUncheckedIndexedAccess` cannot know that — so the accessors
     below are total functions with an honest fallback rather than a sprinkling
     of `!`. ⚠️ An out-of-range bone reads as sitting at the origin, which puts
     it in `other` and leaves it unposed. **That is the safe direction.** */
  const children: number[][] = Array.from({ length: n }, () => []);
  // eslint-disable-next-line security/detect-object-injection -- a loop counter or a fixed 0|1|2 axis index into a plain array, as `legsplit.ts` and `burgeom.ts` do.
  const at = (i: number): RigBone => bones[i] ?? { name: '', parent: -1, world: [0, 0, 0] };
  // eslint-disable-next-line security/detect-object-injection -- a loop counter or a fixed 0|1|2 axis index into a plain array, as `legsplit.ts` and `burgeom.ts` do.
  const kidsOf = (i: number): number[] => children[i] ?? [];

  for (let i = 0; i < n; i++) {
    const p = at(i).parent;
    if (p >= 0 && p < n) kidsOf(p).push(i);
  }

  const along = (i: number) => at(i).world[axes.length];
  const up = (i: number) => at(i).world[axes.up];
  const across = (i: number) => at(i).world[axes.across];

  /* 🔴 WHICH WAY IS FORWARD? The head end is the one with something TALL on it.
     Comparing the extreme bones on the length axis is not enough — a tail and a
     muzzle are both "an end". ✅ So: the highest bone's own along-value decides,
     because a standing quadruped's highest point is its head or its shoulders,
     never its tail. */
  const highest = [...Array(n).keys()].reduce((a, b) => (up(b) > up(a) ? b : a), 0);
  const mid = [...Array(n).keys()].reduce((s, i) => s + along(i), 0) / n;
  const forward = along(highest) >= mid ? 1 : -1;
  /** how far forward, with forward always positive */
  const fwd = (i: number) => along(i) * forward;

  /* ── ① The chains ────────────────────────────────────────────────────────
     A chain is a maximal run of single-child bones ending at a leaf. It stops
     at a branch point, which is exactly where a limb meets the body. */
  const leaves = [...Array(n).keys()].filter((i) => kidsOf(i).length === 0);
  const chainOf = (leaf: number): number[] => {
    const run = [leaf];
    let cur = leaf;
    for (;;) {
      const p = at(cur).parent;
      if (p < 0 || kidsOf(p).length !== 1) break;
      run.push(p);
      cur = p;
    }
    return run.reverse(); // root of the chain first
  };
  const chains = leaves.map(chainOf);
  const tipOf = (c: readonly number[]): number => c[c.length - 1] ?? 0;

  /* ── ② The four lowest tips are the feet ────────────────────────────────
     ⚠️ Lowest TIP, not lowest root: a hip bone can sit at the spine's own
     height, and in rig ② all four do — they share the pelvis's position
     exactly. Judging by the root would have found no legs at all. */
  const byHeight = [...chains].sort((a, b) => up(tipOf(a)) - up(tipOf(b)));
  const legChains = byHeight.slice(0, 4);

  /* ── ③ Fore or hind, left or right ──────────────────────────────────────
     By the DEEPEST bone rather than the root, because in rig ② every leg's
     root is on the centre line and they are told apart only lower down. */
  const legFwd = legChains.map((c) => fwd(tipOf(c)));
  // eslint-disable-next-line security/detect-object-injection -- a loop counter or a fixed 0|1|2 axis index into a plain array, as `legsplit.ts` and `burgeom.ts` do.
  const legOrder = [...legChains.keys()].sort((a, b) => (legFwd[b] ?? 0) - (legFwd[a] ?? 0));
  const isFore = new Set(legOrder.slice(0, 2));

  const legs: QuadAnatomy['legs'] = legChains.map((bonesOfLeg, i) => ({
    bones: bonesOfLeg,
    fore: isFore.has(i),
    /* 🔴 The SIGN is what matters, not the word. Whether +across is the
       animal's left or its right depends on the model's handedness, and
       getting it backwards MIRRORS the gait — which is still a correct walk.
       The diagonal PAIRING is the thing that must be right. */
    side: across(tipOf(bonesOfLeg)) >= 0 ? 1 : -1,
  }));

  const inLeg = new Set(legChains.flat());
  const feet = new Set(legChains.map(tipOf));

  /** does this bone's subtree contain a hoof? */
  const hasFoot = (i: number): boolean => feet.has(i) || kidsOf(i).some(hasFoot);

  /* ── ④ The neck, from the head tip DOWN to where the legs branch off ────
     🔴 **THE OBVIOUS VERSION OF THIS WAS WRONG AND A TEST CAUGHT IT.** Taking
     "the forwardmost chain" gave rig ② an **empty neck**, because in that rig
     the forwardmost chain IS a single ear — two ear tips branch off the head,
     so the head itself is a branch point and never starts a chain at all.
     ⚠️ Stripping the ear then left nothing to turn.
     ✅ **So walk UP from the forwardmost bone instead, and stop at the first
     ancestor that has a LEG hanging off it by another route.** That ancestor is
     the chest, and everything between is the neck. It does not care whether the
     head branches, how deep the chain is, or what anything is called. */
  const nonLeg = [...Array(n).keys()].filter((i) => !inLeg.has(i));
  const headTip = nonLeg.reduce((a, b) => (fwd(b) > fwd(a) ? b : a), nonLeg[0] ?? 0);

  const path: number[] = [headTip];
  for (let cur = headTip; ;) {
    const p = at(cur).parent;
    if (p < 0) break;
    const siblings = kidsOf(p).filter((c) => c !== cur);
    if (siblings.some(hasFoot)) break; // `p` is the chest
    path.push(p);
    cur = p;
  }
  const neckChain = path.reverse();

  /* ⚠️ **A tip with a LEAF SIBLING is an ear, not a neck bone** — yawing one
     ear is not turning a head. Both siblings are kept for an ear wiggle later. */
  const ears: number[] = [];
  let neck = [...neckChain];
  while (neck.length > 0) {
    const tip = neck[neck.length - 1] ?? 0;
    const p = at(tip).parent;
    const siblings = p >= 0 ? kidsOf(p).filter((c) => c !== tip && kidsOf(c).length === 0) : [];
    if (siblings.length === 0) break;
    ears.push(tip, ...siblings);
    neck = neck.slice(0, -1);
  }

  /* ── ⑤ The tail is whatever is left reaching furthest BACK ──────────────
     🔴 `null` rather than a guess when there is none: rig ① has no tail bones,
     and inventing one would wag a leg. */
  const spoken = new Set([...inLeg, ...neckChain, ...ears]);
  const others = chains.filter((c) => !c.some((b) => spoken.has(b)));
  const tailChain =
    others.length > 0 ? others.reduce((a, b) => (fwd(tipOf(b)) < fwd(tipOf(a)) ? b : a)) : null;
  /* A tail points backwards and sits near the centre line. If the leftover
     chain does neither, it is not a tail — it is a brisket bone or a fleece
     bone, and it gets left alone. */
  const acrossSpan = Math.max(...[...Array(n).keys()].map((i) => Math.abs(across(i))));
  /* 🦊 **A chain whose tip is the very BACK of the animal is a tail even when it
     curves aside.** T288: the fox's long brush curls a few centimetres off the
     centre line and the centre-line test alone threw a real five-bone tail
     away. A brisket or fleece bone never reaches the rearmost tenth. */
  const fwds = [...Array(n).keys()].map(fwd);
  const rearmost = Math.min(...fwds);
  const atRear = (i: number) => fwd(i) <= rearmost + (Math.max(...fwds) - rearmost) * 0.1;
  const tail =
    tailChain !== null &&
    fwd(tipOf(tailChain)) < mid * forward &&
    (Math.abs(across(tipOf(tailChain))) < acrossSpan * 0.4 || atRear(tipOf(tailChain)))
      ? tailChain
      : null;

  const claimed = new Set([...inLeg, ...neck, ...ears, ...(tail ?? [])]);
  const spine = [...Array(n).keys()]
    .filter((i) => !claimed.has(i) && kidsOf(i).length > 0)
    .sort((a, b) => fwd(a) - fwd(b));
  const spineSet = new Set(spine);
  const other = [...Array(n).keys()].filter((i) => !claimed.has(i) && !spineSet.has(i));

  return { axes, spine, neck, ears, tail, legs, other };
}

/**
 * 📐 **The gait, and it is Mojang's.** One cosine, diagonal pairs, signs
 * `+ − − +` — the same pattern `quadSwing` already uses, pointed at bones.
 *
 * 🔴 **A diagonal pair moves together**: fore-left with hind-right. Anything
 * else reads as a pantomime horse.
 */
export function legPhase(leg: { readonly fore: boolean; readonly side: 1 | -1 }): 1 | -1 {
  return leg.fore ? leg.side : (-leg.side as 1 | -1);
}

/**
 * 🧭 **How far to turn this model so its NOSE POINTS ALONG +X**, in radians.
 *
 * 🔴 **This game draws a creature at `-yaw` and its heading is `atan2(dz, dx)`,
 * so +X is forward.** ⚠️ **A generator points a body wherever it likes** — the
 * sheep's came back nose along +Z, a quarter turn out.
 *
 * 🎯 **haugur saw what that looks like:** *"they move their legs but thei glide
 * sideways, like they are missing the brain."* **It was the body, not the
 * brain.**
 *
 * ✅ **Measured off the skeleton — the head end against the rump end,
 * flattened** — so the next animal is correct whichever way its generator
 * happened to face it. ⚠️ **Height is dropped on purpose: a head is higher
 * than a rump and that is not a heading.**
 */
export function headingTurn(bones: readonly RigBone[], anatomy: QuadAnatomy): number {
  const head = anatomy.neck[anatomy.neck.length - 1];
  const rump = anatomy.spine[0];
  if (head === undefined || rump === undefined) return 0;
  // eslint-disable-next-line security/detect-object-injection -- indices derived from the anatomy, into a plain array.
  const nose = bones[head]?.world ?? [0, 0, 0];
  // eslint-disable-next-line security/detect-object-injection -- indices derived from the anatomy, into a plain array.
  const back = bones[rump]?.world ?? [0, 0, 0];
  return Math.atan2((nose[2] ?? 0) - (back[2] ?? 0), (nose[0] ?? 0) - (back[0] ?? 0));
}

/**
 * 🧶 **Which bones carry BARE SKIN rather than fleece.**
 *
 * 🎯 **haugur, 2026-09-24, looking at the first rigged flock:** *"color is off,
 * they are the same brown colour from head to toe, not very realistic like
 * that."* **He is right, and the cause is one multiply.**
 *
 * 🔴 **The fleece tint was applied to the WHOLE body.** The model is generated
 * *"a creamy white fleece, a bare pale face, pale hooves"* — so the albedo
 * already tells the face and the legs apart. ⚠️ **Multiplying all of it by the
 * mórautt tint threw that away and painted the animal one colour.**
 *
 * ✅ **The rig knows where the bare parts are**: the head, the ears, and every
 * leg bone below the hip. 🔴 **The hip stays FLEECE** — it sits inside the
 * barrel, under the wool, and tinting it bare would put a pale patch on the
 * flank. 📏 On this sheep the fleece hem measures 0.453 m, which is below the
 * hip and above the knee.
 *
 * 👉 ***Nothing here says sheep.*** A bare-legged, bare-faced quadruped is what
 * a horse, a cow and a goat all are.
 */
export function bareBones(bones: readonly RigBone[], anatomy: QuadAnatomy): ReadonlySet<number> {
  const bare = new Set<number>();
  /* the head is the last bone of the neck; the ears hang off it */
  const headBone = anatomy.neck[anatomy.neck.length - 1];
  if (headBone !== undefined) bare.add(headBone);
  for (const e of anatomy.ears) bare.add(e);

  // eslint-disable-next-line security/detect-object-injection -- a fixed 0|1|2 axis index.
  const up = (i: number): number => bones[i]?.world[anatomy.axes.up] ?? 0;

  for (const leg of anatomy.legs) {
    const hip = leg.bones[0];
    const hoof = leg.bones[leg.bones.length - 1];
    if (hip === undefined || hoof === undefined) continue;
    /**
     * 🧶 **THE FLEECE HEM, AND IT IS MEASURED PER LEG.**
     *
     * 🔴 **Marking every leg bone below the hip as bare turned the hanging
     * belly locks WHITE** — they are weighted to the upper leg, so they took
     * the bare tint with them. ⚠️ **It looked right in a number and wrong in
     * a picture**: a brown sheep with a pale fringe along its underside.
     *
     * ✅ **So the line is HALF WAY DOWN THE LEG**, hip to hoof, measured on
     * each leg separately — a fore leg and a hind leg are different lengths
     * on the same animal. **Above it is wool; below it is bare skin.**
     *
     * ⚠️ **A DIAL at 0.5, and not haugur’s number.** 📏 On the old cut body
     * the hem measured 0.453 m of a 0.85 m animal, which is about half.
     */
    const hem = (up(hip) + up(hoof)) / 2;
    for (let k = 1; k < leg.bones.length; k++) {
      // eslint-disable-next-line security/detect-object-injection -- a loop counter into a plain array.
      const b = leg.bones[k];
      if (b !== undefined && up(b) < hem) bare.add(b);
    }
    /* 🔴 The hoof is ALWAYS bare, whatever the arithmetic says. An animal
       with wool on its feet is a fault nobody would report as a colour bug. */
    bare.add(hoof);
  }
  return bare;
}
